/**
 * StudioCanvas — the single Test-Fit Studio drawing surface.
 *
 * One canvas, two projections of the SAME solver output:
 *  - 'map': world-frame Google Maps hybrid basemap (parcels, exclusions,
 *    components, noise bands, acoustic screens, frontline) — the merged
 *    successor of GenerationLayoutMapPreview;
 *  - 'blueprint': the local-metre plan sheet (grid graticule, dimension
 *    strip, north arrow, scale bar) — the merged successor of
 *    GenerationLayoutPlanSheet, now selection-aware and polygon-exact
 *    (rotated pins and drawn customs render their true rings, not bboxes).
 *
 * Rendering only: every geometry-affecting action flows through the host's
 * patchLayout into the frozen scenario input.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import {
  AlertTriangle,
  Crosshair,
  LocateFixed,
  MapPinned,
  Minus,
  Plus,
  RotateCcw,
} from 'lucide-react';

import {
  latLngForLocalAxes,
  localAxesForPoint,
  localizeLayoutExclusionRings,
  type GenerationParcelTestFitResult,
} from '../../../../lib/generationImpact/generationLayoutGeometry';
import { colorForCategory } from '../../../../config/siteLayerColors';
import type { GenerationTestFitComponentKind } from '../../../../lib/generationImpact/generationTestFit';
import type {
  GenerationAcousticBarrier,
  GenerationLayoutConfiguration,
  NoiseFieldResult,
} from '../../../../types/generationImpact';
import type { SiteBoundaryParcel } from '../../../../types/siteBoundary';
import type { SiteLayer } from '../../../../types/site';
import { SiteBoundaryOverlay, SiteLayerOverlays } from '../../SiteMapLayers';
import { SiteLayerDrawOverlay } from '../../SiteLayerDrawOverlay';
import type { StudioProjection } from './studioStore';
import {
  blueprintBoundsForTarget,
  blueprintScale,
  blueprintToLocal,
  fitBlueprintCamera,
  isStudioFitIssueComponent,
  localToBlueprint,
  mapPointsForTarget,
  niceScaleDistance,
  panBlueprintCamera,
  zoomBlueprintCameraAt,
  type BlueprintCamera,
  type BlueprintFrame,
  type StudioFitTarget,
  type StudioMapCamera,
  type StudioPoint2D,
  type StudioViewportSnapshot,
} from './studioCamera';
import {
  readStudioViewportMemory,
  writeStudioViewportMemory,
} from './studioViewportMemory';
import {
  createGoogleMapsStudioMapViewportAdapter,
  type StudioMapViewportAdapter,
} from './studioMapViewportAdapter';

export const COMPONENT_COLORS: Record<GenerationTestFitComponentKind, string> = {
  'generation-block': '#2563EB',
  cooling: '#0891B2',
  switchyard: '#D97706',
  'ccs-capture': '#7C3AED',
  'ccs-compression': '#9333EA',
  'natural-gas-route': '#64748B',
  'co2-export-route': '#64748B',
  'construction-laydown': '#94A3B8',
  'access-road': '#94A3B8',
  road: '#94A3B8',
  parking: '#CBD5E1',
  building: '#F97316',
  'data-center': '#DB2777',
  generator: '#22C55E',
  custom: '#E11D48',
};

const EXCLUSION_COLOR = '#DC2626';
const FRONTLINE_COLOR = '#F59E0B';
const FLAGGED_COLOR = '#DC2626';
const NOISE_BAND_COLOR = '#fbbf24';
const BARRIER_COLOR = '#22d3ee';
const SELECTION_COLOR = '#38BDF8';
/** Emphasis for a SELECTED non-component overlay (barrier, site layer). */
const SELECTED_OVERLAY_COLOR = '#2dd4bd';
const ISSUE_TRACE_COLOR = '#F59E0B';
/** Muted slate outline + dimmed fill mark a layer the operator has LOCKED
 *  (protected from canvas drags). Lock is a view preference, never geometry. */
const LOCKED_COLOR = '#64748B';
const LOCKED_FILL_OPACITY_SCALE = 0.5;

export function componentFlagged(
  component: GenerationParcelTestFitResult['components'][number],
): boolean {
  return isStudioFitIssueComponent(component);
}

interface PolylineOverlayProps {
  path: { lat: number; lng: number }[];
  color: string;
  dashed?: boolean;
  /** Optional selection (barriers/frontline) — reports modifier-held clicks. */
  onClick?: (additive: boolean) => void;
  selected?: boolean;
}

const PolylineOverlay: React.FC<PolylineOverlayProps> = ({
  path,
  color,
  dashed = false,
  onClick,
  selected = false,
}) => {
  const map = useMap();
  // Ref so a fresh callback identity never rebuilds the overlay.
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;
  useEffect(() => {
    if (!map || path.length < 2 || typeof google === 'undefined' || !google.maps) return;
    const line = new google.maps.Polyline({
      path,
      strokeColor: selected ? SELECTED_OVERLAY_COLOR : color,
      strokeWeight: selected ? 5 : 3,
      clickable: onClickRef.current != null,
      ...(dashed
        ? {
            strokeOpacity: 0,
            icons: [{
              icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeWeight: 3, strokeColor: color },
              offset: '0',
              repeat: '12px',
            }],
          }
        : {}),
      map,
    });
    const listener = onClickRef.current
      ? line.addListener('click', (event: google.maps.MapMouseEvent) => {
          const dom = event?.domEvent as MouseEvent | undefined;
          onClickRef.current?.(Boolean(dom?.shiftKey || dom?.metaKey || dom?.ctrlKey));
        })
      : null;
    return () => {
      listener?.remove();
      line.setMap(null);
    };
  }, [map, path, color, dashed, selected]);
  return null;
};

/** Clickable, selection-aware, optionally draggable component polygon
 *  (imperative Maps pattern — `editable` is never used: it would distort
 *  rigid rectangles vertex-by-vertex; whole-shape drag preserves them). */
const ComponentPolygonOverlay: React.FC<{
  path: readonly { lat: number; lng: number }[];
  color: string;
  fillOpacity: number;
  selected: boolean;
  traced?: boolean;
  draggable?: boolean;
  locked?: boolean;
  onClick?: () => void;
  /** Fires with the dragged shape's ring centroid after a drag ends. */
  onDragEnd?: (centroid: { lat: number; lng: number }) => void;
}> = ({
  path,
  color,
  fillOpacity,
  selected,
  traced = false,
  draggable = false,
  locked = false,
  onClick,
  onDragEnd,
}) => {
  const map = useMap();
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;
  const onDragEndRef = useRef(onDragEnd);
  onDragEndRef.current = onDragEnd;
  useEffect(() => {
    if (!map || path.length < 3 || typeof google === 'undefined' || !google.maps) return;
    const polygon = new google.maps.Polygon({
      paths: [...path],
      strokeColor: traced
        ? ISSUE_TRACE_COLOR
        : selected
          ? SELECTION_COLOR
          : locked
            ? LOCKED_COLOR
            : color,
      strokeWeight: traced ? 5 : selected ? 4 : 2,
      fillColor: locked ? LOCKED_COLOR : color,
      fillOpacity: locked ? fillOpacity * LOCKED_FILL_OPACITY_SCALE : fillOpacity,
      clickable: Boolean(onClickRef.current) || draggable,
      draggable,
      zIndex: traced ? 40 : selected ? 30 : 20,
      map,
    });
    const listeners = [
      polygon.addListener('click', () => onClickRef.current?.()),
      polygon.addListener('dragend', () => {
        const ring = polygon.getPath().getArray();
        if (ring.length === 0) return;
        // Open-ring centroid (the closing vertex is not duplicated by Maps).
        const centroid = ring.reduce(
          (sum, point) => ({ lat: sum.lat + point.lat(), lng: sum.lng + point.lng() }),
          { lat: 0, lng: 0 },
        );
        onDragEndRef.current?.({
          lat: centroid.lat / ring.length,
          lng: centroid.lng / ring.length,
        });
      }),
    ];
    return () => {
      for (const listener of listeners) listener.remove();
      polygon.setMap(null);
    };
  }, [map, path, color, fillOpacity, selected, traced, draggable, locked]);
  return null;
};

/** Forwards plain-map clicks to placement, or clears selection in pointer mode. */
const MapClickCapture: React.FC<{
  onPlaceClick?: (point: { lat: number; lng: number }) => void;
  onBackgroundClick: () => void;
  backgroundSelectionEnabled: boolean;
  adapter?: StudioMapViewportAdapter;
}> = ({ onPlaceClick, onBackgroundClick, backgroundSelectionEnabled, adapter: injectedAdapter }) => {
  const map = useMap();
  const productionAdapter = useMemo(
    () => map ? createGoogleMapsStudioMapViewportAdapter(map) : null,
    [map],
  );
  const adapter = injectedAdapter ?? productionAdapter;
  const onPlaceClickRef = useRef(onPlaceClick);
  onPlaceClickRef.current = onPlaceClick;
  const onBackgroundClickRef = useRef(onBackgroundClick);
  onBackgroundClickRef.current = onBackgroundClick;
  const backgroundSelectionEnabledRef = useRef(backgroundSelectionEnabled);
  backgroundSelectionEnabledRef.current = backgroundSelectionEnabled;
  const suppressClickUntilRef = useRef(0);
  useEffect(() => {
    if (!adapter) return;
    return adapter.onGesture(event => {
      if (event.type === 'drag-start') {
        suppressClickUntilRef.current = Number.POSITIVE_INFINITY;
        return;
      }
      if (event.type === 'drag-end') {
        suppressClickUntilRef.current = Date.now() + 250;
        return;
      }
      if (event.type !== 'click') return;
      if (Date.now() <= suppressClickUntilRef.current) {
        suppressClickUntilRef.current = 0;
        return;
      }
      if (event.point && onPlaceClickRef.current) {
        onPlaceClickRef.current(event.point);
      } else if (backgroundSelectionEnabledRef.current) {
        onBackgroundClickRef.current();
      }
    });
  }, [adapter]);
  return null;
};

export interface StudioCanvasHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  fit: (target?: StudioFitTarget) => void;
  reset: () => void;
  getViewport: () => StudioViewportSnapshot;
}

export interface StudioCanvasProps {
  projection: StudioProjection;
  testFit: GenerationParcelTestFitResult;
  /** True when the scenario has NO placed equipment — the required envelope
   *  rests on default sizing, so the verdict must read as screening-grade. */
  provisionalBasis?: boolean;
  parcels: readonly SiteBoundaryParcel[];
  frontline: GenerationLayoutConfiguration['frontline'];
  noiseField?: NoiseFieldResult | null;
  noiseVisible: boolean;
  acousticBarriers: readonly GenerationAcousticBarrier[];
  /** Primary selection: the fit-selection anchor and drag target. */
  selectedId: string | null;
  /** Full multi-selection set for highlighting (defaults to [selectedId]). */
  selectedIds?: readonly string[];
  /** Background clicks clear selection only while the Select tool is active. */
  selectionMode?: boolean;
  /** Short-lived amber verification trace for issue-to-canvas navigation. */
  traceId?: string | null;
  onSelect: (id: string | null, additive?: boolean) => void;
  /** Active sketch mode for SiteLayerDrawOverlay; null = pointer. */
  drawMode: 'line' | 'polygon' | null;
  drawColor: string;
  onDrawComplete: (coordinates: Array<{ lat: number; lng: number }>) => void;
  onDrawCancel: () => void;
  /** Armed by the click-to-place tools; fires on a plain map click. */
  onPlaceClick?: (point: { lat: number; lng: number }) => void;
  /** True for components the user may drag (selected, movable, editable). */
  canDrag: (componentId: string) => boolean;
  /** Drag commit: the component's new ring centroid in world coordinates. */
  onComponentDragEnd: (componentId: string, centroid: { lat: number; lng: number }) => void;
  /** Committed site-reference layers. Invisible layers neither render nor fit. */
  siteLayers?: readonly SiteLayer[];
  /** Viewer-only opacity per committed reference layer. */
  siteLayerOpacity?: Readonly<Record<string, number>>;
  /** Viewer-only hidden generated/custom component ids. */
  hiddenComponentIds?: ReadonlySet<string>;
  /** Viewer-only locked object ids — dimmed, non-draggable (canDrag also gates). */
  lockedObjectIds?: ReadonlySet<string>;
  /**
   * Stable `${siteId}:${scenarioId}` key for per-context camera restoration.
   * Omit when the host wants mount-local view state only.
   */
  viewportKey?: string;
  /** Optional host-provided seed; local viewport memory wins when available. */
  defaultViewport?: StudioViewportSnapshot;
  /** Fires only after a pan/zoom/fit gesture settles, never per pointer frame. */
  onViewportChange?: (viewport: StudioViewportSnapshot) => void;
  /** Cooperative inline scrolling or greedy full-screen CAD/GIS interaction. */
  gestureMode?: 'cooperative' | 'greedy';
  /** Keeps Map / Blueprint switching directly on the viewport. */
  onProjectionChange?: (projection: StudioProjection) => void;
  /** Compatibility shell can retain its ribbon-only camera surface. */
  showCameraControls?: boolean;
  /** Test seam for camera, resize, idle, and gesture behavior. */
  mapViewportAdapter?: StudioMapViewportAdapter;
}

const SHEET_WIDTH = 1_000;
const SHEET_HEIGHT = 560;
const CAMERA_WHEEL_SETTLE_MS = 180;
/** Fixed right inset (px) for the blueprint north indicator's top-right zone. */
const NORTH_ARROW_RIGHT_INSET_PX = 56;
/** Sheet-border inset. Gives the drawing frame breathing room from the panel edge. */
const SHEET_INSET_PX = 20;

interface ActiveCameraController {
  zoomBy: (steps: number) => void;
  fit: (target: StudioFitTarget) => void;
  reset: () => void;
}

function blueprintComponentClass(
  component: GenerationParcelTestFitResult['components'][number],
): string {
  if (componentFlagged(component)) return 'fill-danger/10 stroke-danger';
  switch (component.rectangle.kind) {
    case 'generation-block': return 'fill-primary/20 stroke-primary';
    case 'cooling': return 'fill-success/10 stroke-success';
    case 'switchyard': return 'fill-text-muted/10 stroke-text-muted';
    case 'ccs-capture':
    case 'ccs-compression': return 'fill-warning/15 stroke-warning';
    case 'natural-gas-route': return 'fill-warning/10 stroke-warning';
    case 'co2-export-route': return 'fill-primary/10 stroke-primary';
    case 'construction-laydown': return 'fill-text-muted/10 stroke-text-muted';
    case 'access-road':
    case 'road': return 'fill-text-muted/15 stroke-text-muted';
    case 'parking': return 'fill-text-muted/10 stroke-text-secondary';
    case 'building': return 'fill-warning/20 stroke-warning';
    case 'data-center': return 'fill-primary/25 stroke-primary';
    case 'generator': return 'fill-success/15 stroke-success';
    case 'custom': return 'fill-danger/5 stroke-text-secondary';
  }
}

export function componentShortLabel(
  component: GenerationParcelTestFitResult['components'][number],
): string {
  switch (component.rectangle.kind) {
    case 'generation-block': return `BLOCK ${component.rectangle.blockNumber ?? ''}`.trim();
    case 'cooling': return 'COOLING';
    case 'switchyard': return 'SWITCHYARD';
    case 'ccs-capture': return 'CAPTURE';
    case 'ccs-compression': return 'CO₂ COMP.';
    case 'natural-gas-route': return 'GAS ROUTE';
    case 'co2-export-route': return 'CO₂ ROUTE';
    case 'construction-laydown': return 'LAYDOWN';
    case 'access-road':
    case 'road': return 'ROAD';
    case 'parking': return 'PARKING';
    case 'building': return 'BUILDING';
    case 'data-center': return 'DATA CENTER';
    case 'generator': return 'GENERATOR';
    case 'custom': return component.rectangle.label.toUpperCase();
  }
}

/**
 * Stable, local-metre-only geometry exposed to browser acceptance tests.
 *
 * SVG path data depends on the restored camera and container dimensions, so it
 * cannot prove that a saved scenario replayed the same engineering geometry.
 * This deliberately excludes world coordinates and rounds only below one
 * micrometre; the E2E can compare every component byte-for-byte across reloads
 * without putting camera state or sensitive site coordinates in the DOM.
 */
export function studioComponentGeometryAttribute(
  rectangle: GenerationParcelTestFitResult['components'][number]['rectangle'],
): string {
  const rounded = (value: number): number => Number(value.toFixed(6));
  return JSON.stringify({
    id: rectangle.id,
    centerM: [rounded(rectangle.xM), rounded(rectangle.yM)],
    sizeM: [rounded(rectangle.widthM), rounded(rectangle.heightM)],
    rotationDeg: rounded(rectangle.rotationDeg ?? 0),
    polygonM: rectangle.polygonM.map(point => [
      rounded(point.xM),
      rounded(point.yM),
    ]),
  });
}

function useObservedBlueprintSize(
  ref: React.RefObject<HTMLDivElement | null>,
): { width: number; height: number; measured: boolean } {
  const [size, setSize] = useState({
    width: SHEET_WIDTH,
    height: SHEET_HEIGHT,
    measured: false,
  });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = (width: number, height: number) => {
      if (!(width > 0) || !(height > 0)) return;
      setSize(current => (
        current.measured
          && Math.abs(current.width - width) < 0.5
          && Math.abs(current.height - height) < 0.5
          ? current
          : {
              // The drafting viewport must match the real host aspect ratio.
              // Minimum CSS sizing belongs to the shell, never the SVG viewBox.
              width: Math.max(1, width),
              height: Math.max(1, height),
              measured: true,
            }
      ));
    };
    const rect = element.getBoundingClientRect();
    measure(rect.width, rect.height);
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(entries => {
          const entry = entries[0];
          if (entry) measure(entry.contentRect.width, entry.contentRect.height);
        })
      : null;
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [ref]);
  return size;
}

function clientToSheetPoint(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
  viewport: { width: number; height: number },
): StudioPoint2D {
  const ctm = typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
  if (ctm && typeof DOMPoint !== 'undefined') {
    try {
      const point = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
      if (Number.isFinite(point.x) && Number.isFinite(point.y)) {
        return { x: point.x, y: point.y };
      }
    } catch {
      // Fall through to the bounding-rectangle projection.
    }
  }
  const rect = svg.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) {
    return {
      x: (clientX - rect.left) / rect.width * viewport.width,
      y: (clientY - rect.top) / rect.height * viewport.height,
    };
  }
  return { x: clientX, y: clientY };
}

interface StudioCameraControlsProps {
  projection: StudioProjection;
  onProjectionChange?: (projection: StudioProjection) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitLayout: () => void;
  onFitParcel: () => void;
  onFitSelection: () => void;
  onFitIssues: () => void;
  onFitAllParcels?: () => void;
  onReset?: () => void;
  selectionAvailable: boolean;
  issuesAvailable: boolean;
  allParcelsAvailable?: boolean;
}

const StudioCameraControls: React.FC<StudioCameraControlsProps> = ({
  projection,
  onProjectionChange,
  onZoomIn,
  onZoomOut,
  onFitLayout,
  onFitParcel,
  onFitSelection,
  onFitIssues,
  onFitAllParcels,
  onReset,
  selectionAvailable,
  issuesAvailable,
  allParcelsAvailable = false,
}) => {
  const buttonClass = 'flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card/95 text-text-secondary shadow-sm backdrop-blur-sm transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-35 max-[599px]:h-11 max-[599px]:w-11';
  return (
    // Reserved top-left zone: projection switch + camera/navigation controls.
    // Keeping them left frees the top-right corner for the north indicator so
    // the two never overlap (previously the north arrow had to dodge this stack).
    // `top-14` clears the sheet's title band (SVG baseline at SHEET_INSET_PX+22)
    // with room to spare — at top-3 this opaque stack sat on top of the title
    // and clipped "FIELD FIT ·" off the front of it.
    <div
      className="pointer-events-auto absolute left-5 top-14 z-10 flex flex-col items-start gap-1.5"
      role="group"
      aria-label="Canvas camera controls"
      data-testid="studio-camera-controls"
    >
      {onProjectionChange && (
        <div
          className="inline-flex overflow-hidden rounded-lg border border-border bg-card/95 shadow-sm backdrop-blur-sm"
          role="group"
          aria-label="Canvas projection"
          data-testid="studio-camera-projection-group"
        >
          {(['map', 'blueprint'] as const).map((option, index) => (
            <button
              key={option}
              type="button"
              onClick={() => onProjectionChange(option)}
              aria-pressed={projection === option}
              className={`min-h-9 min-w-12 px-2 text-[9px] font-bold uppercase tracking-wide transition-colors focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary max-[599px]:min-h-11 ${
                index > 0 ? 'border-l border-border' : ''
              } ${
                projection === option
                  ? 'bg-primary/15 text-primary'
                  : 'text-text-secondary hover:bg-background/80 hover:text-text-primary'
              }`}
              data-testid={`studio-camera-projection-${option}`}
            >
              {option === 'map' ? 'Map' : 'Blueprint'}
            </button>
          ))}
        </div>
      )}
      <CameraButton className={buttonClass} onClick={onZoomIn} label="Zoom in" testId="studio-camera-zoom-in">
        <Plus className="h-4 w-4" aria-hidden />
      </CameraButton>
      <CameraButton className={buttonClass} onClick={onZoomOut} label="Zoom out" testId="studio-camera-zoom-out">
        <Minus className="h-4 w-4" aria-hidden />
      </CameraButton>
      <CameraButton className={buttonClass} onClick={onFitLayout} label="Fit layout" testId="studio-camera-fit-layout">
        <LocateFixed className="h-4 w-4" aria-hidden />
      </CameraButton>
      <CameraButton className={buttonClass} onClick={onFitParcel} label="Fit target parcel" testId="studio-camera-fit-parcel">
        <MapPinned className="h-4 w-4" aria-hidden />
      </CameraButton>
      <CameraButton
        className={buttonClass}
        onClick={onFitSelection}
        disabled={!selectionAvailable}
        label="Fit selected component"
        hint={selectionAvailable ? undefined : 'Select a component to fit it'}
        testId="studio-camera-fit-selection"
      >
        <Crosshair className="h-4 w-4" aria-hidden />
      </CameraButton>
      <CameraButton
        className={buttonClass}
        onClick={onFitIssues}
        disabled={!issuesAvailable}
        label="Fit components with issues"
        hint={issuesAvailable ? undefined : 'No flagged components'}
        testId="studio-camera-fit-issues"
      >
        <AlertTriangle className="h-4 w-4" aria-hidden />
      </CameraButton>
      {onFitAllParcels && (
        <CameraButton
          className={buttonClass}
          onClick={onFitAllParcels}
          disabled={!allParcelsAvailable}
          label="Fit all parcels"
          hint={allParcelsAvailable ? undefined : 'Only one parcel is available'}
          testId="studio-camera-fit-all-parcels"
        >
          <Crosshair className="h-4 w-4" aria-hidden />
        </CameraButton>
      )}
      {onReset && (
        <CameraButton
          className={buttonClass}
          onClick={onReset}
          label="Reset Blueprint to 100%"
          ariaLabel="Reset Blueprint to 100 percent"
          testId="studio-camera-reset"
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
        </CameraButton>
      )}
    </div>
  );
};

/**
 * One camera-rail button with a styled hover/focus tooltip.
 *
 * The rail is icon-only, so every button needs a label on hover. The native
 * `title` attribute is slow (~1s) and unstyled, so the tooltip is rendered
 * inline and revealed on `group-hover`/`group-focus-within` — it appears
 * instantly, matches the app surface, and stays available to keyboard users.
 * A disabled button still shows its tooltip (that is where `hint` explains WHY
 * it is disabled), so the wrapper — not the button — owns the hover group.
 */
const CameraButton: React.FC<{
  className: string;
  onClick: () => void;
  label: string;
  /** Replaces the label while disabled, to explain the reason. */
  hint?: string;
  ariaLabel?: string;
  disabled?: boolean;
  testId: string;
  children: React.ReactNode;
}> = ({ className, onClick, label, hint, ariaLabel, disabled = false, testId, children }) => (
  <span className="group relative flex">
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      data-testid={testId}
    >
      {children}
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute left-full top-1/2 z-20 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-card px-2 py-1 text-[10px] font-semibold text-text-primary opacity-0 shadow-md transition-opacity duration-100 group-hover:opacity-100 group-focus-within:opacity-100"
      data-testid={`${testId}-tooltip`}
    >
      {hint ?? label}
    </span>
  </span>
);

interface BlueprintProjectionProps {
  testFit: GenerationParcelTestFitResult;
  /** True when the scenario has NO placed equipment — the required envelope
   *  rests on default sizing, so the verdict must read as screening-grade. */
  provisionalBasis?: boolean;
  selectedId: string | null;
  selectedIdSet: ReadonlySet<string>;
  traceId?: string | null;
  selectionMode: boolean;
  hiddenComponentIds: ReadonlySet<string>;
  lockedObjectIds: ReadonlySet<string>;
  onSelect: (id: string | null, additive?: boolean) => void;
  canDrag: (componentId: string) => boolean;
  onComponentDragEnd: (componentId: string, centroid: { lat: number; lng: number }) => void;
  initialCamera?: BlueprintCamera;
  onCameraSettled: (camera: BlueprintCamera) => void;
  onRegisterController: (controller: ActiveCameraController | null) => void;
  visibleSiteLayerCount: number;
  /** Visible site-reference layers, drawn on the blueprint in the local frame. */
  visibleSiteLayers: readonly SiteLayer[];
  siteLayerOpacity: Readonly<Record<string, number>>;
  /** Acoustic screens + frontline, drawn on the blueprint for Map/Blueprint parity. */
  acousticBarriers: readonly GenerationAcousticBarrier[];
  frontline: GenerationLayoutConfiguration['frontline'];
  /** Single-click place/connect on the blueprint — fired with WORLD coords, so
   *  the same tools work as on the map. Present only when a place/connect tool
   *  is armed (the host gates it), so a plain tap fires it here. */
  onPlaceClick?: (point: { lat: number; lng: number }) => void;
  /** Multi-point draw (frontline / barrier / road / polygon) on the blueprint:
   *  click to add vertices, double-click or Enter to finish, Escape to cancel.
   *  Completion reports WORLD coords, identical to the map draw overlay. */
  drawMode: 'line' | 'polygon' | null;
  drawColor: string;
  onDrawComplete: (coordinates: Array<{ lat: number; lng: number }>) => void;
  onDrawCancel: () => void;
  onProjectionChange?: (projection: StudioProjection) => void;
  showCameraControls: boolean;
}

interface BlueprintPointerGesture {
  pointerId: number;
  start: StudioPoint2D;
  startCamera: BlueprintCamera;
  moved: boolean;
}

interface BlueprintComponentGesture {
  pointerId: number;
  componentId: string;
  start: StudioPoint2D;
  camera: BlueprintCamera;
  moved: boolean;
}

interface BlueprintPinchGesture {
  startDistance: number;
  startCentroid: StudioPoint2D;
  startCamera: BlueprintCamera;
}

/** Responsive local-metre blueprint with projection-native pan/zoom and drag. */
const BlueprintProjection: React.FC<BlueprintProjectionProps> = ({
  testFit,
  provisionalBasis,
  selectedId,
  selectedIdSet,
  traceId,
  selectionMode,
  hiddenComponentIds,
  lockedObjectIds,
  onSelect,
  canDrag,
  onComponentDragEnd,
  initialCamera,
  onCameraSettled,
  onRegisterController,
  visibleSiteLayerCount,
  visibleSiteLayers,
  siteLayerOpacity,
  acousticBarriers,
  frontline,
  onPlaceClick,
  drawMode,
  drawColor,
  onDrawComplete,
  onDrawCancel,
  onProjectionChange,
  showCameraControls,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const viewport = useObservedBlueprintSize(hostRef);
  const frame = useMemo<BlueprintFrame>(() => ({
    x: 50,
    y: 48,
    width: Math.max(120, viewport.width - 100),
    height: Math.max(100, viewport.height - 140),
  }), [viewport.height, viewport.width]);
  const layoutBounds = useMemo(
    () => blueprintBoundsForTarget(testFit, 'layout', selectedId),
    [selectedId, testFit],
  );
  const defaultFit = useMemo(
    () => layoutBounds ? fitBlueprintCamera(layoutBounds, frame) : null,
    [frame, layoutBounds],
  );
  const fallbackCamera = defaultFit ?? {
    centerXM: 0,
    centerYM: 0,
    metresPerPixel: 1_000 / Math.max(1, frame.width),
  };
  const [camera, setCamera] = useState<BlueprintCamera>(
    () => initialCamera ?? fallbackCamera,
  );
  const cameraRef = useRef(camera);
  cameraRef.current = camera;
  // Multi-point draw on the blueprint. Vertices are kept in LOCAL METRES (the
  // projection-native frame) so pan/zoom between clicks never moves a placed
  // vertex; they convert to world coords once, on completion.
  const [drawPoints, setDrawPoints] = useState<Array<{ xM: number; yM: number }>>([]);
  useEffect(() => {
    // Disarming (or switching) the tool clears any half-finished sketch.
    if (drawMode === null) setDrawPoints([]);
  }, [drawMode]);
  const userChangedCameraRef = useRef(initialCamera != null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panRef = useRef<BlueprintPointerGesture | null>(null);
  const componentDragRef = useRef<BlueprintComponentGesture | null>(null);
  const touchPointsRef = useRef(new Map<number, StudioPoint2D>());
  const pinchRef = useRef<BlueprintPinchGesture | null>(null);
  const [dragPreview, setDragPreview] = useState<{
    componentId: string;
    dxM: number;
    dyM: number;
  } | null>(null);
  const suppressClickRef = useRef<string | 'background' | null>(null);

  const settleCamera = useCallback((next: BlueprintCamera, debounced = false) => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    if (debounced) {
      settleTimerRef.current = setTimeout(() => {
        settleTimerRef.current = null;
        onCameraSettled(cameraRef.current);
      }, CAMERA_WHEEL_SETTLE_MS);
    } else {
      onCameraSettled(next);
    }
  }, [onCameraSettled]);

  useEffect(() => {
    if (!viewport.measured || userChangedCameraRef.current || !defaultFit) return;
    setCamera(defaultFit);
    cameraRef.current = defaultFit;
    userChangedCameraRef.current = true;
  }, [
    defaultFit?.centerXM,
    defaultFit?.centerYM,
    defaultFit?.metresPerPixel,
    viewport.measured,
  ]);

  useEffect(() => () => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    onCameraSettled(cameraRef.current);
  }, [onCameraSettled]);

  const fit = useCallback((target: StudioFitTarget) => {
    const bounds = blueprintBoundsForTarget(testFit, target, selectedId);
    if (!bounds) return;
    const fitted = fitBlueprintCamera(
      bounds,
      frame,
      target === 'selection' ? 48 : 40,
    );
    const basis = defaultFit ?? fitted;
    const next = {
      ...fitted,
      metresPerPixel: Math.min(
        basis.metresPerPixel * 4,
        Math.max(basis.metresPerPixel / 8, fitted.metresPerPixel),
      ),
    };
    userChangedCameraRef.current = true;
    setCamera(next);
    cameraRef.current = next;
    settleCamera(next);
  }, [defaultFit, frame, selectedId, settleCamera, testFit]);

  const zoomBy = useCallback((steps: number) => {
    const current = cameraRef.current;
    const basis = defaultFit ?? current;
    const next = zoomBlueprintCameraAt(
      current,
      frame,
      { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 },
      2 ** (steps / 4),
      {
        minMetresPerPixel: basis.metresPerPixel / 8,
        maxMetresPerPixel: basis.metresPerPixel * 4,
      },
    );
    userChangedCameraRef.current = true;
    setCamera(next);
    cameraRef.current = next;
    settleCamera(next);
  }, [defaultFit, frame, settleCamera]);

  const reset = useCallback(() => fit('layout'), [fit]);

  useEffect(() => {
    const controller: ActiveCameraController = { zoomBy, fit, reset };
    onRegisterController(controller);
    return () => onRegisterController(null);
  }, [fit, onRegisterController, reset, zoomBy]);

  if (!testFit.plan) {
    return (
      <div className="relative h-full min-h-56 bg-background" data-testid="generation-layout-plan-sheet">
        <div className="flex h-full items-center justify-center p-6 text-center text-xs text-text-muted">
          {testFit.error ?? 'Enter a usable parcel and layout basis to build the planning sheet.'}
        </div>
        {showCameraControls && (
          <StudioCameraControls
            projection="blueprint"
            onProjectionChange={onProjectionChange}
            onZoomIn={() => zoomBy(1)}
            onZoomOut={() => zoomBy(-1)}
            onFitLayout={() => fit('layout')}
            onFitParcel={() => fit('target-parcel')}
            onFitSelection={() => fit('selection')}
            onFitIssues={() => fit('issues')}
            onReset={reset}
            selectionAvailable={false}
            issuesAvailable={false}
          />
        )}
      </div>
    );
  }

  const plan = testFit.plan;
  const envelope = testFit.envelope;
  const scale = blueprintScale(camera, frame);
  const project = (point: { xM: number; yM: number }, dxM = 0, dyM = 0) => (
    localToBlueprint(
      { xM: point.xM + dxM, yM: point.yM + dyM },
      camera,
      frame,
    )
  );
  const pathFor = (
    points: readonly { xM: number; yM: number }[],
    dxM = 0,
    dyM = 0,
  ) => points.map((point, index) => {
    const projected = project(point, dxM, dyM);
    return `${index === 0 ? 'M' : 'L'} ${projected.x} ${projected.y}`;
  }).join(' ');
  const parcelPath = envelope ? pathFor(envelope.parcelPolygonM) : '';
  const exclusionPaths = testFit.parcel && envelope && testFit.layout.exclusions?.zones.length
    ? localizeLayoutExclusionRings(
        testFit.layout.exclusions.zones,
        testFit.parcel,
        envelope,
        testFit.layout.rotationDeg,
      ).map(ring => pathFor(ring))
    : [];
  // Site-reference layers, projected into the SAME local frame the components
  // use (via `localAxesForPoint`, the inverse of the drag→world transform), so
  // the blueprint honours layer visibility/opacity instead of a "map only"
  // notice. Geometry is correct by construction — it reuses the component
  // projection pipeline (`pathFor`/`project`).
  const siteLayerBlueprintPaths = visibleSiteLayers.map((layer) => {
    const localPoints = layer.geometry.coordinates.map(coord =>
      localAxesForPoint(coord, testFit.anchor, testFit.layout.rotationDeg));
    return {
      id: layer.id,
      geometryType: layer.geometry.type,
      d: pathFor(localPoints),
      first: localPoints[0] ? project(localPoints[0]) : null,
      color: colorForCategory(layer.category, layer.color),
      opacity: Math.max(0.1, Math.min(1, siteLayerOpacity[layer.id] ?? 1)),
    };
  });
  // Acoustic screens + frontline projected into the SAME local frame — Map and
  // Blueprint now draw (and select) the same elements; the toggle only swaps
  // the background. Barriers reuse the component projection pipeline.
  const barrierBlueprintPaths = acousticBarriers.map((barrier) => {
    const localPoints = barrier.path.map(coord =>
      localAxesForPoint(coord, testFit.anchor, testFit.layout.rotationDeg));
    return {
      id: barrier.id,
      isBuilding: barrier.kind === 'building',
      d: pathFor(localPoints),
    };
  });
  const frontlineBlueprintPath = frontline
    ? pathFor([frontline.start, frontline.end].map(coord =>
        localAxesForPoint(coord, testFit.anchor, testFit.layout.rotationDeg)))
    : null;
  const northAngleDeg = -testFit.layout.rotationDeg;
  const scaleBarMetres = niceScaleDistance(camera.metresPerPixel * frame.width * 0.18);
  const scaleBarWidth = scaleBarMetres * scale;
  const gridMinorMetres = niceScaleDistance(camera.metresPerPixel * 36);
  const gridMajorMetres = gridMinorMetres * 5;
  const gridMinorPx = Math.max(8, gridMinorMetres * scale);
  const gridMajorPx = Math.max(gridMinorPx, gridMajorMetres * scale);
  const issuesAvailable = testFit.components.some(componentFlagged);
  const selectionAvailable = testFit.components.some(
    component => component.rectangle.id === selectedId,
  );

  const handleWheel = (event: React.WheelEvent<SVGSVGElement>) => {
    event.preventDefault();
    const cursor = clientToSheetPoint(
      event.currentTarget,
      event.clientX,
      event.clientY,
      viewport,
    );
    const basis = defaultFit ?? cameraRef.current;
    const factor = Math.min(2, Math.max(0.5, Math.exp(-event.deltaY * 0.002)));
    const next = zoomBlueprintCameraAt(
      cameraRef.current,
      frame,
      cursor,
      factor,
      {
        minMetresPerPixel: basis.metresPerPixel / 8,
        maxMetresPerPixel: basis.metresPerPixel * 4,
      },
    );
    userChangedCameraRef.current = true;
    setCamera(next);
    cameraRef.current = next;
    settleCamera(next, true);
  };

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0 && event.button !== 1) return;
    const start = clientToSheetPoint(
      event.currentTarget,
      event.clientX,
      event.clientY,
      viewport,
    );
    if (event.pointerType === 'touch') {
      touchPointsRef.current.set(event.pointerId, start);
      const points = [...touchPointsRef.current.values()];
      if (points.length >= 2) {
        const first = points[0]!;
        const second = points[1]!;
        pinchRef.current = {
          startDistance: Math.max(1, Math.hypot(second.x - first.x, second.y - first.y)),
          startCentroid: {
            x: (first.x + second.x) / 2,
            y: (first.y + second.y) / 2,
          },
          startCamera: cameraRef.current,
        };
        panRef.current = null;
      }
    }
    panRef.current = {
      pointerId: event.pointerId,
      start,
      startCamera: cameraRef.current,
      moved: false,
    };
    if (pinchRef.current) panRef.current = null;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const handleComponentPointerDown = (
    event: React.PointerEvent<SVGGElement>,
    componentId: string,
  ) => {
    event.stopPropagation();
    // Plain press selects immediately so the SAME gesture can drag (`canDrag`
    // requires `selectedId === componentId`).
    //
    // An ADDITIVE press must NOT select here: this `<g>` also carries an
    // `onClick` that calls `onSelect(id, additive)`, and pointerdown always
    // fires before click. Selecting non-additively here would collapse the
    // multi-selection to this one component, and then the click's
    // `toggleSelect` would switch it straight back off — so shift-clicking a
    // second component could never reach two selected. Leave the additive
    // toggle to the click handler so it runs EXACTLY once.
    const additive = event.shiftKey || event.metaKey || event.ctrlKey;
    if (!additive) onSelect(componentId);
    if (event.button !== 0 || !canDrag(componentId)) return;
    const svg = svgRef.current;
    if (!svg) return;
    componentDragRef.current = {
      pointerId: event.pointerId,
      componentId,
      start: clientToSheetPoint(svg, event.clientX, event.clientY, viewport),
      camera: cameraRef.current,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragPreview({ componentId, dxM: 0, dyM: 0 });
    event.preventDefault();
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const componentGesture = componentDragRef.current;
    if (componentGesture?.pointerId === event.pointerId) {
      const point = clientToSheetPoint(
        event.currentTarget,
        event.clientX,
        event.clientY,
        viewport,
      );
      const gestureScale = blueprintScale(componentGesture.camera, frame);
      const dxM = (point.x - componentGesture.start.x) / gestureScale;
      const dyM = -(point.y - componentGesture.start.y) / gestureScale;
      componentGesture.moved ||= Math.hypot(
        point.x - componentGesture.start.x,
        point.y - componentGesture.start.y,
      ) >= 2;
      setDragPreview({ componentId: componentGesture.componentId, dxM, dyM });
      return;
    }
    if (event.pointerType === 'touch' && touchPointsRef.current.has(event.pointerId)) {
      const point = clientToSheetPoint(
        event.currentTarget,
        event.clientX,
        event.clientY,
        viewport,
      );
      touchPointsRef.current.set(event.pointerId, point);
      const pinch = pinchRef.current;
      const points = [...touchPointsRef.current.values()];
      if (pinch && points.length >= 2) {
        const first = points[0]!;
        const second = points[1]!;
        const distance = Math.max(1, Math.hypot(second.x - first.x, second.y - first.y));
        const centroid = {
          x: (first.x + second.x) / 2,
          y: (first.y + second.y) / 2,
        };
        const basis = defaultFit ?? pinch.startCamera;
        const zoomed = zoomBlueprintCameraAt(
          pinch.startCamera,
          frame,
          pinch.startCentroid,
          distance / pinch.startDistance,
          {
            minMetresPerPixel: basis.metresPerPixel / 8,
            maxMetresPerPixel: basis.metresPerPixel * 4,
          },
        );
        const next = panBlueprintCamera(
          zoomed,
          frame,
          centroid.x - pinch.startCentroid.x,
          centroid.y - pinch.startCentroid.y,
        );
        userChangedCameraRef.current = true;
        setCamera(next);
        cameraRef.current = next;
        return;
      }
    }
    const pan = panRef.current;
    if (pan?.pointerId !== event.pointerId) return;
    const point = clientToSheetPoint(
      event.currentTarget,
      event.clientX,
      event.clientY,
      viewport,
    );
    const dx = point.x - pan.start.x;
    const dy = point.y - pan.start.y;
    pan.moved ||= Math.hypot(dx, dy) >= 2;
    const next = panBlueprintCamera(pan.startCamera, frame, dx, dy);
    userChangedCameraRef.current = true;
    setCamera(next);
    cameraRef.current = next;
  };

  const finishPointer = (event: React.PointerEvent<SVGSVGElement>, cancelled: boolean) => {
    const componentGesture = componentDragRef.current;
    if (componentGesture?.pointerId === event.pointerId) {
      if (!cancelled && componentGesture.moved && dragPreview) {
        const component = testFit.components.find(
          candidate => candidate.rectangle.id === componentGesture.componentId,
        );
        if (component && Math.hypot(dragPreview.dxM, dragPreview.dyM) >= 0.05) {
          const draggedCenter = {
            xM: component.rectangle.xM + dragPreview.dxM,
            yM: component.rectangle.yM + dragPreview.dyM,
          };
          onComponentDragEnd(
            componentGesture.componentId,
            latLngForLocalAxes(
              draggedCenter,
              testFit.anchor,
              testFit.layout.rotationDeg,
            ),
          );
        }
        suppressClickRef.current = componentGesture.componentId;
      }
      componentDragRef.current = null;
      setDragPreview(null);
    }

    if (event.pointerType === 'touch') {
      touchPointsRef.current.delete(event.pointerId);
      if (pinchRef.current && touchPointsRef.current.size < 2) {
        pinchRef.current = null;
        settleCamera(cameraRef.current);
        const remaining = [...touchPointsRef.current.entries()][0];
        panRef.current = remaining
          ? {
              pointerId: remaining[0],
              start: remaining[1],
              startCamera: cameraRef.current,
              moved: false,
            }
          : null;
      }
    }

    const pan = panRef.current;
    if (pan?.pointerId === event.pointerId) {
      if (pan.moved) {
        suppressClickRef.current = 'background';
        settleCamera(cameraRef.current);
      } else if (!cancelled && !componentDragRef.current && (drawMode !== null || onPlaceClick)) {
        // A clean tap. Sheet px → local metre (`blueprintToLocal`, the inverse of
        // the render projection) → world (`latLngForLocalAxes`), so the blueprint
        // acts exactly where the map would. Suppress the follow-up deselect click.
        const sheet = clientToSheetPoint(event.currentTarget, event.clientX, event.clientY, viewport);
        const localPoint = blueprintToLocal(sheet, cameraRef.current, frame);
        if (drawMode !== null) {
          // Multi-point draw: accumulate a vertex (double-click finishes).
          setDrawPoints(current => [...current, localPoint]);
        } else {
          onPlaceClick?.(latLngForLocalAxes(localPoint, testFit.anchor, testFit.layout.rotationDeg));
        }
        suppressClickRef.current = 'background';
      }
      panRef.current = null;
    }
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      /* capture may belong to a child component group */
    }
  };

  /** Minimum vertices a sketch needs before it can be committed. */
  const drawMinimumPoints = drawMode === 'polygon' ? 3 : 2;
  const completeDrawing = (): void => {
    if (drawMode === null || drawPoints.length < drawMinimumPoints) return;
    const coordinates = drawPoints.map(point =>
      latLngForLocalAxes(point, testFit.anchor, testFit.layout.rotationDeg));
    setDrawPoints([]);
    onDrawComplete(coordinates);
  };
  const cancelDrawing = (): void => {
    setDrawPoints([]);
    onDrawCancel();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.matches('input, textarea, select, [contenteditable="true"]')) return;
    if (drawMode !== null) {
      // Draw-mode keys take precedence over the camera shortcuts.
      if (event.key === 'Enter') {
        event.preventDefault();
        completeDrawing();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelDrawing();
        return;
      }
      if (event.key === 'Backspace') {
        event.preventDefault();
        setDrawPoints(current => current.slice(0, -1));
        return;
      }
    }
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomBy(1);
    } else if (event.key === '-') {
      event.preventDefault();
      zoomBy(-1);
    } else if (event.key.toLowerCase() === 'f') {
      event.preventDefault();
      fit(event.shiftKey ? 'selection' : 'layout');
    }
  };

  return (
    <div
      ref={hostRef}
      className="relative h-full min-h-56 w-full overflow-hidden bg-background"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      data-testid="generation-layout-plan-sheet"
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${viewport.width} ${viewport.height}`}
        className={`block h-full w-full bg-background ${panRef.current ? 'cursor-grabbing' : 'cursor-grab'}`}
        role="img"
        aria-label="Parcel test-fit blueprint showing placed components in the local plant frame"
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={event => finishPointer(event, false)}
        onPointerCancel={event => finishPointer(event, true)}
        onClick={() => {
          if (suppressClickRef.current === 'background') {
            suppressClickRef.current = null;
            return;
          }
          if (drawMode !== null) return;
          if (selectionMode) onSelect(null);
        }}
        onDoubleClick={(event) => {
          if (drawMode === null) return;
          event.preventDefault();
          completeDrawing();
        }}
        style={{ touchAction: 'none' }}
      >
        <defs>
          <pattern id="studio-blueprint-grid" width={gridMinorPx} height={gridMinorPx} patternUnits="userSpaceOnUse">
            <path d={`M ${gridMinorPx} 0 L 0 0 0 ${gridMinorPx}`} className="fill-none stroke-border/40" strokeWidth="0.6" />
          </pattern>
          <pattern id="studio-blueprint-grid-major" width={gridMajorPx} height={gridMajorPx} patternUnits="userSpaceOnUse">
            <path d={`M ${gridMajorPx} 0 L 0 0 0 ${gridMajorPx}`} className="fill-none stroke-border/70" strokeWidth="0.7" />
          </pattern>
          <clipPath id="studio-blueprint-camera-clip">
            <rect x={frame.x} y={frame.y} width={frame.width} height={frame.height} />
          </clipPath>
          <style>{`
            .studio-selection-ants { animation: studio-march 0.8s linear infinite; }
            .studio-issue-trace { animation: studio-issue-pulse 1.6s ease-out both; }
            @keyframes studio-march { to { stroke-dashoffset: -12; } }
            @keyframes studio-issue-pulse {
              0%, 35% { opacity: 1; stroke-width: 5; }
              100% { opacity: 0.15; stroke-width: 2; }
            }
            @media (prefers-reduced-motion: reduce) {
              .studio-selection-ants, .studio-issue-trace { animation: none; }
            }
          `}</style>
        </defs>
        <rect width={viewport.width} height={viewport.height} fill="url(#studio-blueprint-grid)" />
        <rect width={viewport.width} height={viewport.height} fill="url(#studio-blueprint-grid-major)" />

        {/* Sheet border. Insets give the drawing frame room to breathe against
            the panel edge, and the rounded corners match the app's card radius
            (--radius 0.5rem) instead of a hard CAD square. */}
        <rect x={SHEET_INSET_PX} y={SHEET_INSET_PX} width={viewport.width - SHEET_INSET_PX * 2} height={viewport.height - SHEET_INSET_PX * 2} rx="8" className="fill-none stroke-border" strokeWidth="1.4" />
        <rect x={SHEET_INSET_PX + 6} y={SHEET_INSET_PX + 6} width={viewport.width - (SHEET_INSET_PX + 6) * 2} height={viewport.height - (SHEET_INSET_PX + 6) * 2} rx="6" className="fill-none stroke-border/50" strokeWidth="0.6" />
        {([
          [SHEET_INSET_PX, SHEET_INSET_PX],
          [viewport.width - SHEET_INSET_PX, SHEET_INSET_PX],
          [SHEET_INSET_PX, viewport.height - SHEET_INSET_PX],
          [viewport.width - SHEET_INSET_PX, viewport.height - SHEET_INSET_PX],
        ] as const).map(([cx, cy], index) => (
          <g key={index} aria-hidden>
            <line x1={cx - 6} y1={cy} x2={cx + 6} y2={cy} className="stroke-text-muted/70" strokeWidth="1" />
            <line x1={cx} y1={cy - 6} x2={cx} y2={cy + 6} className="stroke-text-muted/70" strokeWidth="1" />
          </g>
        ))}

        <text x={SHEET_INSET_PX + 14} y={SHEET_INSET_PX + 22} className="fill-text-muted text-[12px] font-bold tracking-[0.2em]">FIELD FIT · A-01 · SCREENING</text>
        <text x={viewport.width - SHEET_INSET_PX - 14} y={SHEET_INSET_PX + 22} textAnchor="end" className="fill-text-muted text-[11px]">
          ROT {testFit.layout.rotationDeg.toFixed(0)}°
        </text>
        {/* The centered reference-layer notice shares the top margin with the
            left title and right ROT readout; only show it when the sheet is
            wide enough to avoid colliding with the title (the Layers panel
            carries the same count regardless). */}
        {visibleSiteLayerCount > 0 && viewport.width >= 900 && (
          <text
            x={viewport.width / 2}
            y={SHEET_INSET_PX + 22}
            textAnchor="middle"
            className="fill-text-muted text-[9px] font-bold uppercase tracking-wider"
            data-testid="studio-blueprint-reference-layers"
          >
            {visibleSiteLayerCount} reference layer{visibleSiteLayerCount === 1 ? '' : 's'}
          </text>
        )}

        <g
          // Reserved top-right zone. The camera controls now live top-left, so
          // the north indicator uses a fixed right inset instead of dodging them.
          transform={`translate(${viewport.width - NORTH_ARROW_RIGHT_INSET_PX} 84) rotate(${northAngleDeg})`}
          aria-hidden
          data-testid="studio-blueprint-north-arrow"
        >
          <path d="M 0 -18 L 6 8 L 0 3 L -6 8 Z" className="fill-text-secondary" />
          <text x="0" y="22" textAnchor="middle" className="fill-text-muted text-[9px] font-bold">N</text>
        </g>

        <g clipPath="url(#studio-blueprint-camera-clip)">
          {/* Site-reference layers as a bottom underlay (below equipment). */}
          <g data-testid="studio-blueprint-site-layers">
            {siteLayerBlueprintPaths.map(layer => {
              const selected = selectedIdSet.has(layer.id);
              const selectLayer = (event: React.MouseEvent): void => {
                event.stopPropagation();
                onSelect(layer.id, event.shiftKey || event.metaKey || event.ctrlKey);
              };
              return layer.geometryType === 'point'
                ? layer.first && (
                    <circle
                      key={layer.id}
                      cx={layer.first.x}
                      cy={layer.first.y}
                      r={selected ? 6 : 4}
                      fill={layer.color}
                      fillOpacity={layer.opacity}
                      stroke={selected ? '#2dd4bd' : 'none'}
                      strokeWidth={selected ? 2 : 0}
                      onClick={selectLayer}
                      className="cursor-pointer"
                      data-testid={`studio-blueprint-site-layer-${layer.id}`}
                    />
                  )
                : (
                    <path
                      key={layer.id}
                      d={layer.geometryType === 'polygon' ? `${layer.d} Z` : layer.d}
                      fill={layer.geometryType === 'polygon' ? layer.color : 'none'}
                      fillOpacity={layer.geometryType === 'polygon' ? 0.14 * layer.opacity : 0}
                      stroke={selected ? '#2dd4bd' : layer.color}
                      strokeOpacity={selected ? 1 : 0.75 * layer.opacity}
                      strokeWidth={selected ? 3 : 1.5}
                      onClick={selectLayer}
                      className="cursor-pointer"
                      data-testid={`studio-blueprint-site-layer-${layer.id}`}
                    />
                  );
            })}
          </g>
          {envelope && (() => {
            const topLeft = project({ xM: -envelope.widthM / 2, yM: envelope.heightM / 2 });
            return (
              <>
                <rect
                  x={topLeft.x}
                  y={topLeft.y}
                  width={envelope.widthM * scale}
                  height={envelope.heightM * scale}
                  className="fill-none stroke-text-muted/50"
                  strokeDasharray="8 5"
                  strokeWidth="1.2"
                />
                <path d={`${parcelPath} Z`} className="fill-primary/5 stroke-primary/70" strokeWidth="2" />
              </>
            );
          })()}

          {exclusionPaths.map((path, index) => (
            <path
              key={`exclusion-${index}`}
              d={`${path} Z`}
              className="fill-danger/15 stroke-danger/70"
              strokeWidth="1.5"
              strokeDasharray="6 4"
            />
          ))}

          {testFit.components
            .filter(component => !hiddenComponentIds.has(component.rectangle.id))
            .map((component) => {
            const rectangle = component.rectangle;
            const preview = dragPreview?.componentId === rectangle.id
              ? dragPreview
              : { dxM: 0, dyM: 0 };
            const ring = pathFor(rectangle.polygonM, preview.dxM, preview.dyM);
            const selected = selectedIdSet.has(rectangle.id);
            const locked = lockedObjectIds.has(rectangle.id);
            const traced = traceId === rectangle.id;
            const width = rectangle.widthM * scale;
            const height = rectangle.heightM * scale;
            const showLabel = width >= 72 && height >= 30;
            const labelPoint = project(
              { xM: rectangle.xM, yM: rectangle.yM },
              preview.dxM,
              preview.dyM,
            );
            return (
              <g
                key={rectangle.id}
                role="button"
                tabIndex={0}
                aria-label={`${rectangle.label}, ${rectangle.areaAcres.toFixed(1)} acres`}
                aria-pressed={selected}
                onPointerDown={event => handleComponentPointerDown(event, rectangle.id)}
                onClick={(event) => {
                  event.stopPropagation();
                  if (suppressClickRef.current === rectangle.id) {
                    suppressClickRef.current = null;
                    return;
                  }
                  onSelect(rectangle.id, event.shiftKey || event.metaKey || event.ctrlKey);
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  onSelect(rectangle.id, event.shiftKey || event.metaKey || event.ctrlKey);
                }}
                className={`${canDrag(rectangle.id) ? 'cursor-move' : 'cursor-pointer'}${locked ? ' opacity-60' : ''}`}
                data-testid={`studio-blueprint-component-${rectangle.id}`}
                data-locked={locked ? 'true' : undefined}
                data-studio-component-geometry={studioComponentGeometryAttribute(rectangle)}
              >
                <path
                  d={`${ring} Z`}
                  className={blueprintComponentClass(component)}
                  strokeWidth={selected ? 3.5 : 2}
                  {...(rectangle.placement === 'pinned' ? { strokeDasharray: '7 3' } : {})}
                />
                {locked && !selected && (
                  <path
                    d={`${ring} Z`}
                    fill="none"
                    stroke={LOCKED_COLOR}
                    strokeWidth="2"
                    pointerEvents="none"
                  />
                )}
                {selected && (
                  <path
                    d={`${ring} Z`}
                    fill="none"
                    stroke={SELECTION_COLOR}
                    strokeWidth="1.5"
                    strokeDasharray="6 6"
                    className="studio-selection-ants"
                  />
                )}
                {traced && (
                  <path
                    d={`${ring} Z`}
                    fill="none"
                    stroke={ISSUE_TRACE_COLOR}
                    strokeWidth="5"
                    className="studio-issue-trace"
                    pointerEvents="none"
                  />
                )}
                {showLabel && (
                  <>
                    <text x={labelPoint.x} y={labelPoint.y - 2} textAnchor="middle" className="fill-text-primary text-[11px] font-bold tracking-wider">
                      {componentShortLabel(component)}
                    </text>
                    <text x={labelPoint.x} y={labelPoint.y + 14} textAnchor="middle" className="fill-text-muted text-[9px]">
                      {rectangle.areaAcres.toFixed(1)} ac
                      {rectangle.placement === 'pinned' ? ' · pinned' : ''}
                      {locked ? ' · locked' : ''}
                    </text>
                  </>
                )}
              </g>
            );
          })}

          {/* Acoustic screens — drawn + selectable on the blueprint (Map parity). */}
          {barrierBlueprintPaths.map((barrier) => {
            const selected = selectedIdSet.has(barrier.id);
            return (
              <path
                key={barrier.id}
                d={barrier.isBuilding ? `${barrier.d} Z` : barrier.d}
                fill={barrier.isBuilding ? BARRIER_COLOR : 'none'}
                fillOpacity={barrier.isBuilding ? 0.15 : 0}
                stroke={selected ? '#2dd4bd' : BARRIER_COLOR}
                strokeWidth={selected ? 3.5 : 2}
                strokeOpacity={selected ? 1 : 0.85}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(barrier.id, event.shiftKey || event.metaKey || event.ctrlKey);
                }}
                className="cursor-pointer"
                data-testid={`studio-blueprint-barrier-${barrier.id}`}
              />
            );
          })}

          {/* Frontline (row-orientation axis) — Map parity. */}
          {frontlineBlueprintPath && (
            <path
              d={frontlineBlueprintPath}
              fill="none"
              stroke={FRONTLINE_COLOR}
              strokeWidth={2}
              strokeDasharray="7 4"
              data-testid="studio-blueprint-frontline"
            />
          )}

          {/* In-progress sketch (multi-point draw parity with the map). */}
          {drawMode !== null && drawPoints.length > 0 && (
            <g data-testid="studio-blueprint-draw-preview" pointerEvents="none">
              <path
                d={drawMode === 'polygon' && drawPoints.length >= 3
                  ? `${pathFor(drawPoints)} Z`
                  : pathFor(drawPoints)}
                fill={drawMode === 'polygon' ? drawColor : 'none'}
                fillOpacity={drawMode === 'polygon' ? 0.15 : 0}
                stroke={drawColor}
                strokeWidth={2}
                strokeDasharray="6 4"
              />
              {drawPoints.map((point, index) => {
                const projected = project(point);
                return (
                  <circle
                    key={`${projected.x}-${projected.y}-${index}`}
                    cx={projected.x}
                    cy={projected.y}
                    r={3.5}
                    fill={drawColor}
                    data-testid={`studio-blueprint-draw-vertex-${index}`}
                  />
                );
              })}
            </g>
          )}
        </g>

        <g transform={`translate(40 ${viewport.height - 40})`} aria-hidden>
          <line x1="0" y1="0" x2={scaleBarWidth} y2="0" className="stroke-text-secondary" strokeWidth="2" />
          <line x1="0" y1="-4" x2="0" y2="4" className="stroke-text-secondary" strokeWidth="2" />
          <line x1={scaleBarWidth} y1="-4" x2={scaleBarWidth} y2="4" className="stroke-text-secondary" strokeWidth="2" />
          <text x={scaleBarWidth / 2} y="-6" textAnchor="middle" className="fill-text-muted text-[9px]">{scaleBarMetres} m</text>
        </g>

        <line x1="40" y1={viewport.height - 24} x2={viewport.width - 40} y2={viewport.height - 24} className="stroke-border" strokeWidth="1" />
        <text x="40" y={viewport.height - 8} className="fill-text-muted text-[10px]">PARCEL</text>
        <text x="98" y={viewport.height - 8} className="fill-text-primary text-[11px] font-bold">
          {envelope ? `${envelope.widthM.toFixed(0)} × ${envelope.heightM.toFixed(0)} m` : 'N/A'}
        </text>
        <text x={viewport.width * 0.43} y={viewport.height - 8} className="fill-text-muted text-[10px]">
          {provisionalBasis ? 'REQUIRED (PROVISIONAL)' : 'REQUIRED'}
        </text>
        <text x={viewport.width * 0.55} y={viewport.height - 8} className={`text-[11px] font-bold ${provisionalBasis ? 'fill-warning' : 'fill-text-primary'}`}>
          {provisionalBasis ? '≈ ' : ''}{plan.requiredEnvelope.widthM.toFixed(0)} × {plan.requiredEnvelope.heightM.toFixed(0)} m
        </text>
        <text x={viewport.width - 40} y={viewport.height - 8} textAnchor="end" className={`text-[11px] font-bold ${provisionalBasis && testFit.status !== 'fits' ? 'fill-warning' : testFit.status === 'fits' ? 'fill-success' : testFit.status === 'partial' ? 'fill-danger' : 'fill-warning'}`}>
          {provisionalBasis && testFit.status !== 'fits'
            ? 'PROVISIONAL · DEFINE EQUIPMENT'
            : testFit.status === 'fits'
            ? 'CONTAINED + SETBACK'
            : testFit.status === 'partial'
              ? testFit.parcelEdgeSetbackSatisfied === false && (testFit.outsideAcres ?? 0) <= 0.01
                ? 'SETBACK SHORTFALL'
                : 'FIT ISSUE'
              : 'UNVERIFIED'}
        </text>
      </svg>
      {showCameraControls && (
        <StudioCameraControls
          projection="blueprint"
          onProjectionChange={onProjectionChange}
          onZoomIn={() => zoomBy(1)}
          onZoomOut={() => zoomBy(-1)}
          onFitLayout={() => fit('layout')}
          onFitParcel={() => fit('target-parcel')}
          onFitSelection={() => fit('selection')}
          onFitIssues={() => fit('issues')}
          onReset={reset}
          selectionAvailable={selectionAvailable}
          issuesAvailable={issuesAvailable}
        />
      )}
    </div>
  );
};

interface StudioMapCameraBridgeProps {
  testFit: GenerationParcelTestFitResult;
  /** True when the scenario has NO placed equipment — the required envelope
   *  rests on default sizing, so the verdict must read as screening-grade. */
  provisionalBasis?: boolean;
  parcels: readonly SiteBoundaryParcel[];
  selectedId: string | null;
  visibleSiteLayers: readonly SiteLayer[];
  initialCamera?: StudioMapCamera;
  onCameraSettled: (camera: StudioMapCamera) => void;
  onRegisterController: (controller: ActiveCameraController | null) => void;
  adapter?: StudioMapViewportAdapter;
}

/**
 * Projection-native Google Maps camera bridge.
 *
 * Camera state is sampled on `idle` rather than every `bounds_changed` frame.
 * Programmatic fits are also captured by the same path, and cleanup performs
 * a final synchronous sample before the map projection unmounts.
 */
const StudioMapCameraBridge: React.FC<StudioMapCameraBridgeProps> = ({
  testFit,
  provisionalBasis,
  parcels,
  selectedId,
  visibleSiteLayers,
  initialCamera,
  onCameraSettled,
  onRegisterController,
  adapter: injectedAdapter,
}) => {
  const map = useMap();
  const productionAdapter = useMemo(
    () => map ? createGoogleMapsStudioMapViewportAdapter(map) : null,
    [map],
  );
  const adapter = injectedAdapter ?? productionAdapter;
  const testFitRef = useRef(testFit);
  testFitRef.current = testFit;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const visibleSiteLayersRef = useRef(visibleSiteLayers);
  visibleSiteLayersRef.current = visibleSiteLayers;
  const parcelsRef = useRef(parcels);
  parcelsRef.current = parcels;
  const onCameraSettledRef = useRef(onCameraSettled);
  onCameraSettledRef.current = onCameraSettled;
  const initialisedAdapterRef = useRef<StudioMapViewportAdapter | null>(null);

  const captureCamera = useCallback((): StudioMapCamera | null => {
    if (!adapter) return null;
    const center = adapter.getCenter();
    const zoom = adapter.getZoom();
    if (!center || zoom == null || !Number.isFinite(zoom)) return null;
    return { center, zoom };
  }, [adapter]);

  const pointsForTarget = useCallback((target: StudioFitTarget) => {
    if (target === 'all-parcels') {
      return parcelsRef.current.flatMap(parcel => parcel.polygon);
    }
    const points = mapPointsForTarget(
      testFitRef.current,
      target,
      selectedIdRef.current,
    );
    if (target !== 'layout') return points;
    return [
      ...points,
      ...visibleSiteLayersRef.current.flatMap(layer => (
        layer.visible === false ? [] : layer.geometry.coordinates
      )),
    ].filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  }, []);

  const fit = useCallback((target: StudioFitTarget) => {
    if (!adapter) return;
    const points = pointsForTarget(target);
    if (points.length === 0) return;
    adapter.fit(points, {
      paddingPx: target === 'selection' ? 48 : 40,
      fallbackZoom: target === 'selection' ? 19 : 17,
      ...(target === 'selection' ? { maxZoom: 20 } : {}),
    });
  }, [adapter, pointsForTarget]);

  const zoomBy = useCallback((steps: number) => {
    if (!adapter) return;
    const current = adapter.getZoom() ?? 15;
    adapter.setZoom(Math.min(24, Math.max(0, current + steps)));
  }, [adapter]);

  const reset = useCallback(() => fit('layout'), [fit]);

  useEffect(() => {
    if (!adapter) return;
    const controller: ActiveCameraController = { zoomBy, fit, reset };
    onRegisterController(controller);
    return () => onRegisterController(null);
  }, [adapter, fit, onRegisterController, reset, zoomBy]);

  useEffect(() => {
    if (!adapter || initialisedAdapterRef.current === adapter) return;
    initialisedAdapterRef.current = adapter;
    if (initialCamera) {
      adapter.setCenter(initialCamera.center);
      adapter.setZoom(initialCamera.zoom);
    } else {
      fit('layout');
    }
  }, [adapter, fit, initialCamera]);

  useEffect(() => {
    if (!adapter) return;
    const persist = () => {
      const camera = captureCamera();
      if (camera) onCameraSettledRef.current(camera);
    };
    const unsubscribe = adapter.onIdle(persist);
    return () => {
      persist();
      unsubscribe();
    };
  }, [adapter, captureCamera]);

  return null;
};

const StudioMapResizeBridge: React.FC<{
  adapter?: StudioMapViewportAdapter;
}> = ({ adapter: injectedAdapter }) => {
  const map = useMap();
  const productionAdapter = useMemo(
    () => map ? createGoogleMapsStudioMapViewportAdapter(map) : null,
    [map],
  );
  const adapter = injectedAdapter ?? productionAdapter;
  useEffect(() => {
    if (!adapter) return;
    const notifyResize = () => adapter.resize();
    notifyResize();
    const container = adapter.getContainerElement();
    const observer = container && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(notifyResize)
      : null;
    if (container) observer?.observe(container);
    window.addEventListener('resize', notifyResize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', notifyResize);
    };
  }, [adapter]);
  return null;
};

export const StudioCanvas = forwardRef<StudioCanvasHandle, StudioCanvasProps>(({
  projection,
  testFit,
  parcels,
  frontline,
  noiseField,
  noiseVisible,
  acousticBarriers,
  selectedId,
  selectedIds,
  traceId,
  selectionMode = true,
  onSelect,
  drawMode,
  drawColor,
  onDrawComplete,
  onDrawCancel,
  onPlaceClick,
  canDrag,
  onComponentDragEnd,
  siteLayers = [],
  siteLayerOpacity = {},
  hiddenComponentIds = new Set<string>(),
  lockedObjectIds = new Set<string>(),
  viewportKey,
  defaultViewport,
  onViewportChange,
  gestureMode = 'greedy',
  mapViewportAdapter,
  onProjectionChange,
  showCameraControls = true,
}, forwardedRef) => {
  const anchor = testFit.anchor;
  // Highlight the whole multi-selection; fall back to the primary id.
  const selectedIdSet = useMemo(
    () => new Set(selectedIds ?? (selectedId ? [selectedId] : [])),
    [selectedIds, selectedId],
  );
  const exclusionZones = testFit.layout.exclusions?.zones ?? [];
  const visibleSiteLayers = useMemo(
    () => siteLayers.filter(layer => layer.visible !== false),
    [siteLayers],
  );
  const controllerRef = useRef<ActiveCameraController | null>(null);
  const viewportRef = useRef<StudioViewportSnapshot>({});
  const viewportIdentityRef = useRef<string | null>(null);
  const cameraIdentity = viewportKey ?? testFit.parcel?.id ?? 'mount-local';
  if (viewportIdentityRef.current !== cameraIdentity) {
    viewportIdentityRef.current = cameraIdentity;
    viewportRef.current = (
      viewportKey ? readStudioViewportMemory(viewportKey) : null
    ) ?? defaultViewport ?? {};
  }

  const registerController = useCallback((controller: ActiveCameraController | null) => {
    controllerRef.current = controller;
  }, []);
  const publishViewport = useCallback((
    kind: 'blueprint' | 'map',
    camera: BlueprintCamera | StudioMapCamera,
  ) => {
    const next: StudioViewportSnapshot = kind === 'blueprint'
      ? { ...viewportRef.current, blueprint: camera as BlueprintCamera }
      : { ...viewportRef.current, map: camera as StudioMapCamera };
    viewportRef.current = next;
    if (viewportKey) writeStudioViewportMemory(viewportKey, next);
    onViewportChange?.(next);
  }, [onViewportChange, viewportKey]);
  const publishBlueprintCamera = useCallback(
    (camera: BlueprintCamera) => publishViewport('blueprint', camera),
    [publishViewport],
  );
  const publishMapCamera = useCallback(
    (camera: StudioMapCamera) => publishViewport('map', camera),
    [publishViewport],
  );

  useImperativeHandle(forwardedRef, () => ({
    zoomIn: () => controllerRef.current?.zoomBy(1),
    zoomOut: () => controllerRef.current?.zoomBy(-1),
    fit: (target = 'layout') => controllerRef.current?.fit(target),
    reset: () => controllerRef.current?.reset(),
    getViewport: () => ({ ...viewportRef.current }),
  }), []);

  if (projection === 'blueprint') {
    return (
      <BlueprintProjection
        key={`blueprint:${cameraIdentity}`}
        testFit={testFit}
        selectedId={selectedId}
        selectedIdSet={selectedIdSet}
        traceId={traceId}
        selectionMode={selectionMode}
        hiddenComponentIds={hiddenComponentIds}
        lockedObjectIds={lockedObjectIds}
        onSelect={onSelect}
        canDrag={canDrag}
        onComponentDragEnd={onComponentDragEnd}
        initialCamera={viewportRef.current.blueprint}
        onCameraSettled={publishBlueprintCamera}
        onRegisterController={registerController}
        visibleSiteLayerCount={visibleSiteLayers.length}
        visibleSiteLayers={visibleSiteLayers}
        siteLayerOpacity={siteLayerOpacity}
        acousticBarriers={acousticBarriers}
        frontline={frontline}
        {...(onPlaceClick ? { onPlaceClick } : {})}
        drawMode={drawMode}
        drawColor={drawColor}
        onDrawComplete={onDrawComplete}
        onDrawCancel={onDrawCancel}
        onProjectionChange={onProjectionChange}
        showCameraControls={showCameraControls}
      />
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      <GoogleMap
        key={`map:${cameraIdentity}`}
        mapId="generation-layout-preview-map"
        defaultCenter={viewportRef.current.map?.center ?? anchor}
        defaultZoom={viewportRef.current.map?.zoom ?? 15}
        gestureHandling={gestureMode}
        mapTypeId="hybrid"
        mapTypeControl={false}
        fullscreenControl={false}
        streetViewControl={false}
        style={{ width: '100%', height: '100%' }}
      >
        <StudioMapCameraBridge
          testFit={testFit}
          parcels={parcels}
          selectedId={selectedId}
          visibleSiteLayers={visibleSiteLayers}
          initialCamera={viewportRef.current.map}
          onCameraSettled={publishMapCamera}
          onRegisterController={registerController}
          adapter={mapViewportAdapter}
        />
        <StudioMapResizeBridge adapter={mapViewportAdapter} />
        {parcels.map(parcel => (
          <SiteBoundaryOverlay
            key={parcel.id}
            path={parcel.polygon}
            strokeColor={parcel.id === testFit.parcel?.id ? '#D97706' : '#78716C'}
            fillOpacity={0.06}
          />
        ))}
        {exclusionZones.flatMap(zone => zone.polygons.map((ring, ringIndex) => (
          <SiteBoundaryOverlay
            key={`${zone.id}-${ringIndex}`}
            path={ring}
            strokeColor={EXCLUSION_COLOR}
            fillOpacity={0.3}
          />
        )))}
        <MapClickCapture
          onPlaceClick={onPlaceClick}
          onBackgroundClick={() => onSelect(null)}
          backgroundSelectionEnabled={
            selectionMode && drawMode === null && onPlaceClick == null
          }
          adapter={mapViewportAdapter}
        />
        {visibleSiteLayers.map((layer) => {
          const opacity = Math.max(0.1, Math.min(1, siteLayerOpacity[layer.id] ?? 1));
          return (
            <SiteLayerOverlays
              key={layer.id}
              layers={[layer]}
              fillOpacity={0.14 * opacity}
              strokeOpacity={0.75 * opacity}
              strokeWeight={1.5}
              onLayerClick={onSelect}
              selectedLayerIds={selectedIdSet}
            />
          );
        })}
        {testFit.components
          .filter(component => !hiddenComponentIds.has(component.rectangle.id))
          .map(component => (
          <ComponentPolygonOverlay
            key={component.rectangle.id}
            path={component.coordinates}
            color={componentFlagged(component)
              ? FLAGGED_COLOR
              : COMPONENT_COLORS[component.rectangle.kind]}
            fillOpacity={component.rectangle.lifecycle === 'permanent' ? 0.5 : 0.2}
            selected={selectedIdSet.has(component.rectangle.id)}
            traced={traceId === component.rectangle.id}
            draggable={canDrag(component.rectangle.id)}
            locked={lockedObjectIds.has(component.rectangle.id)}
            onClick={() => onSelect(component.rectangle.id)}
            onDragEnd={(centroid) => onComponentDragEnd(component.rectangle.id, centroid)}
          />
          ))}
        {/* Noise dissipation bands — faintest at the lowest level, deepening
            +0.05 per band (matches the Site Map layer ramp). */}
        {noiseVisible && noiseField?.bands.map((band, bandIndex) =>
          band.rings.map((ring, ringIndex) => (
            <SiteBoundaryOverlay
              key={`noise-${band.levelDbA}-${ringIndex}`}
              path={ring}
              strokeColor={NOISE_BAND_COLOR}
              fillOpacity={Math.min(0.4, 0.1 + 0.05 * bandIndex)}
            />
          )),
        )}
        {/* Acoustic screens: walls/berms as cyan polylines, buildings as rings.
            Selectable on the map exactly as on the blueprint. */}
        {acousticBarriers.map((barrier) => barrier.kind === 'building'
          ? (
            <SiteBoundaryOverlay
              key={barrier.id}
              path={barrier.path}
              strokeColor={selectedIdSet.has(barrier.id) ? SELECTED_OVERLAY_COLOR : BARRIER_COLOR}
              fillOpacity={0.15}
            />
          )
          : (
            <PolylineOverlay
              key={barrier.id}
              path={barrier.path}
              color={BARRIER_COLOR}
              selected={selectedIdSet.has(barrier.id)}
              onClick={(additive) => onSelect(barrier.id, additive)}
            />
          ))}
        {frontline && (
          <PolylineOverlay
            path={[frontline.start, frontline.end]}
            color={FRONTLINE_COLOR}
            dashed
          />
        )}
        <SiteLayerDrawOverlay
          mode={drawMode}
          strokeColor={drawColor}
          onComplete={onDrawComplete}
          onCancel={onDrawCancel}
        />
      </GoogleMap>
      {showCameraControls && (
        <StudioCameraControls
          projection="map"
          onProjectionChange={onProjectionChange}
          onZoomIn={() => controllerRef.current?.zoomBy(1)}
          onZoomOut={() => controllerRef.current?.zoomBy(-1)}
          onFitLayout={() => controllerRef.current?.fit('layout')}
          onFitParcel={() => controllerRef.current?.fit('target-parcel')}
          onFitSelection={() => controllerRef.current?.fit('selection')}
          onFitIssues={() => controllerRef.current?.fit('issues')}
          onFitAllParcels={() => controllerRef.current?.fit('all-parcels')}
          selectionAvailable={testFit.components.some(
            component => component.rectangle.id === selectedId,
          )}
          issuesAvailable={testFit.components.some(componentFlagged)}
          allParcelsAvailable={parcels.length > 1}
        />
      )}
    </div>
  );
});

StudioCanvas.displayName = 'StudioCanvas';
